import React, { useEffect, useState } from 'react'
import styled from 'styled-components';

import { ReactComponent as SmallChat } from "../../Assets/icons/small-chats-icons.svg";
import { ReactComponent as SmallPhone } from "../../Assets/icons/small-phone-icons.svg";
import { ReactComponent as ChatNameIcon } from "../../Assets/icons/chatNameInputIcon.svg";
import { useAuthService } from '../../Context/AuthContext';
import LanguageBtn from './LanguageBtn';
import { useTranslate } from '@tolgee/react';
import PrimaryButton from '../GenericComponents/PrimaryButton';
import { useNotify } from '../../Helpers/Notifications';
import languageSyncManager from '../../Helpers/callHelper/LanguageSyncHelper';

const OnboardRegisteredUser = ({ onComplete }) => {
  const { t } = useTranslate();
  const { notifySuccess, notifyError } = useNotify();

  const {
    translationsLanguages,
    callTranslationsLanguages,
    getCallTranslationsLanguages,
    getTranslationsLanguages,
    user,
    updateUser,
    handleLoginUser
  } = useAuthService();

  const [name, setName] = useState(user?.name || "");
  const [language, setLanguage] = useState(user?.language || "");
  const [callLanguage, setCallLanguage] = useState(user?.call_language || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleNameChange = (e) => {
    const { value } = e.currentTarget;
    setName(value);
  };

  const handleSave = () => {
    setIsLoading(true);
    var objectData = {};

    if (name && name !== user?.name) {
      objectData.name = name;
    }
    if (language && language !== user?.language) {
      objectData.language = language;
    }
    if (callLanguage && callLanguage !== user?.call_language) {
      objectData.call_language = callLanguage;
    }

    updateUser(
      objectData,
      function (response) {
        handleLoginUser(response.data.user);

        if (callLanguage && window.electron?.notifyLanguageChanged) {
          window.electron.notifyLanguageChanged(callLanguage);
        }
        if (callLanguage) {
          languageSyncManager.notifyLanguageChanged(callLanguage);
        }

        notifySuccess(`${t('profile.profile_updated')}`);
        setIsLoading(false);
        if (onComplete) {
          onComplete();
        }
      },
      function (error) {
        console.log(error, "error");
        notifyError(`${t('general.something_wrong')}`);
        setIsLoading(false);
      }
    );
  };

  const isFormValid = () => {
    const isNameValid = name && name.trim().length >= 3;
    const isLanguageValid = language && language !== "";
    const isCallLanguageValid = callLanguage && callLanguage !== "";

    return isNameValid && isLanguageValid && isCallLanguageValid;
  };

  useEffect(() => {
    getCallTranslationsLanguages(() => {}, () => {});
    getTranslationsLanguages(() => {}, () => {});
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setLanguage(user.language || "");
      setCallLanguage(user.call_language || "");
    }
  }, [user]);

  return (
    <Container>
      <Content>
        <Card>
          <GroupTitle>
            <h1>{t('onboardingPage.app_preference')}</h1>
            <p>{t('onboardingPage.complete_profile_subtitle')}</p>
          </GroupTitle>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t('register.chat_language')}</GroupDescriptionText>
            </GroupDescription>
            <LanguageBtn
              value={language}
              setValue={setLanguage}
              valuesList={translationsLanguages}
              placeholder={t('register.chat_language_placeholder')}
              placeholderIcon={<SmallChat />}
            />
          </Group>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t('onboardingPage.calls_language')}</GroupDescriptionText>
            </GroupDescription>
            <LanguageBtn
              value={callLanguage}
              setValue={setCallLanguage}
              valuesList={callTranslationsLanguages}
              placeholder={t('onboardingPage.select_calls_placeholder')}
              placeholderIcon={<SmallPhone />}
              callLanguage
            />
          </Group>
          <Group>
            <GroupDescription>
              <GroupDescriptionText>{t('general.your_name')}</GroupDescriptionText>
            </GroupDescription>
            <CustomInputWrapper>
              <ChatNameIcon />
              <input
                placeholder={t('register.name_placeholder')}
                onChange={(e) => handleNameChange(e)}
                value={name}
                name="name"
                maxLength={100}
              />
            </CustomInputWrapper>
          </Group>

          <Group style={{ alignItems: 'center', justifyContent: "center" }}>
            <PrimaryButton
              style={{ padding: "16px", width: "100%" }}
              disabled={!isFormValid() || isLoading}
              id="onboard-save-button"
              onClick={handleSave}
            >
              {t('general.save')}
            </PrimaryButton>
          </Group>
        </Card>
      </Content>
    </Container>
  );
};

export default OnboardRegisteredUser;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  height: 100%;
  width: 100%;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.menu.background};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  width: 100%;
  gap: 40px;
  margin: auto;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  h1 {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 32px;
    font-weight: 600;
    line-height: 39px;
    letter-spacing: 0px;
    text-align: left;
  }
`;

const CustomInputWrapper = styled.div`
  display: flex;
  width: 100%;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  align-items: center;
  padding: 16px;
  border-radius: 4px;
  gap: 10px;

  input {
    width: 100%;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    background: ${({ theme }) => theme.menu.groupContentBackground};
    border: none;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    &:focus {
      outline: none;
    }
    &::placeholder {
      color: ${({ theme }) => theme.menu.primaryTextColor};
      opacity: 0.6;
    }
  }
`;

const GroupTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;

  h1 {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 32px;
    font-weight: 600;
    line-height: 39px;
    letter-spacing: 0px;
    text-align: center;
  }

  p {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: center;
  }
`;

const Content = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 32px;
  overflow: auto;
`;

const GroupDescription = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: -10px;

  p {
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
`;

const GroupDescriptionText = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0px;
  text-align: left;
`;
